/* eslint-disable @atlassian/relay/graphql-naming */
import React, {
	useCallback,
	useMemo,
	useEffect,
	useRef,
	useState,
	type PropsWithChildren,
	forwardRef,
} from 'react';

import { createPortal } from 'react-dom';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import {
	graphql,
	usePaginationFragment,
	usePreloadedQuery,
	useRefetchableFragment,
	useFragment,
	type EntryPointProps,
} from 'react-relay';
import { v4 as uuid } from 'uuid';
// eslint-disable-next-line jira/restricted/@jira+issue__with-container-width
import withContainerWidth from '@jira/issue__with-container-width/src/index.tsx';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import CustomizeIcon from '@atlaskit/icon/core/customize';
import { Box, xcss } from '@atlaskit/primitives';
import { JiraFieldSetViewAri } from '@atlassian/ari/jira/field-set-view';
import logger from '@atlassian/jira-common-util-logging/src/log.tsx';
import { useFlagsService, toFlagId } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import {
	useIssueKey,
	useIsFullPageIssueView,
} from '@atlassian/jira-issue-context-service/src/main.tsx';
import type { CreatedIssues } from '@atlassian/jira-issue-create/src/common/types/index.tsx';
import { getIssueDiscoveryUrl } from '@atlassian/jira-issue-create/src/common/utils/issue-discovery/index.tsx';
import SuccessFlagWithContext from '@atlassian/jira-issue-create/src/ui/modal/success-flag/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';

import { IssueContext } from '@atlassian/jira-issue-field-optional-editing-state-manager/src/index.tsx';
import { HIDE_DONE_SUBTASKS_FILTER } from '@atlassian/jira-issue-shared-types/src/common/types/user-preferences-type.tsx';
import { ColumnPickerContainer } from '@atlassian/jira-issue-table-column-picker/src/controllers/column-picker-open-state/index.tsx';
import { useUserPreferencesValue } from '@atlassian/jira-issue-user-preference-services/src/main.tsx';
import type {
	AsyncLoadOptions,
	SelectValue,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import {
	HEADER_HEIGHT,
	REORDER_COLUMNS,
	ROW_HEIGHT,
	SORT_ASC,
	SORT_DESC,
	SORT_NONE,
} from '@atlassian/jira-native-issue-table/src/common/constants.tsx';
import type {
	ColumnConfigChangeType,
	SortOrder,
} from '@atlassian/jira-native-issue-table/src/common/types.tsx';
import NativeIssueTable from '@atlassian/jira-native-issue-table/src/ui/NativeIssueTableWithSelectedIssuesProvider.tsx';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { useDispatch, useSelector } from '@atlassian/jira-react-redux/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useColumnLoader } from '@atlassian/jira-issue-view-child-issues-panel/src/services/use-column-loader/index.tsx';
import { useColumnResize } from '@atlassian/jira-issue-view-child-issues-panel/src/services/use-column-resize/index.tsx';
import { useColumnsChange } from '@atlassian/jira-issue-view-child-issues-panel/src/services/use-columns-change/index.tsx';
import { useColumnReset } from '@atlassian/jira-issue-view-child-issues-panel/src/services/use-column-reset/index.tsx';
import { useIssuesRanker } from '@atlassian/jira-issue-view-child-issues-panel/src/services/use-issues-ranker/index.tsx';
import {
	ColumnPicker,
	type ColumnPickerApi,
} from '@atlassian/jira-issue-view-child-issues-panel/src/ui/column-picker/index.tsx';
import { ChildIssuesPanelErrorBoundaryWrapper } from '@atlassian/jira-issue-view-child-issues-panel/src/ui/error-boundaries/index.tsx';

import { expVal } from '@atlassian/jira-feature-experiments';
import {
	CHILD_ISSUES_PAGE_SIZE,
	DEFAULT_FIELD_SET_VIEW_ID,
	SORT_OPTION,
	CHILD_ISSUES_PANEL_TYPE,
} from '@atlassian/jira-issue-view-child-issues-panel/src/common/constants.tsx';
import { useUserPreferredWorkItemLayout } from '@atlassian/jira-issue-view-layout/src/services/main.tsx';
import { useChildIssuesSortBy as useChildIssuesSortByRelay } from '@atlassian/jira-issue-view-child-issues-panel/src/common/controllers/main.tsx';
import { useChildIssuesSortBy } from '../../../../controllers/main.tsx';
import {
	getIsChildrenIssuesPanel,
	getIsClassicSubtaskPanel,
	getIsSimplifiedProject,
} from '../../../../state/context/selectors.tsx';
import {
	getRecentlyCreatedIssue,
	getRecentlyLinkedChildIssue,
} from '../../../../state/entities/selectors.tsx';
import { updateRecentCreatedChildIssueOnUnMount } from '../../../../state/entities/actions.tsx';
import type { CreatedIssue } from '../../../../state/entities/types.tsx';
import { useRefetchWithoutSuspend } from '../services/use-refetch-without-suspend/index.tsx';
import { useEventHandlers } from '../utils/use-event-handlers.tsx';

import {
	JQLBuilder,
	createConfigurableChildIssuesPanelQueryVariables,
	getColumnConfigOverrides,
	onChildIssueLoadErrorFlag,
} from '../utils/index.tsx';
import type { ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_fieldSetViewQueryByProject$key } from './__generated__/ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_fieldSetViewQueryByProject.graphql';
import type { ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_childIssuesPanelData$key } from './__generated__/ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_childIssuesPanelData.graphql';
import type { ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_data$key } from './__generated__/ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_data.graphql';
import type { ui_main_ConfigurableChildIssuesPanelWithEntryPointQuery } from './__generated__/ui_main_ConfigurableChildIssuesPanelWithEntryPointQuery.graphql';
import type { ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_IssueSearchPaginationQuery } from './__generated__/ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_IssueSearchPaginationQuery.graphql';
import ConfigurableChildIssuesPanelWithFragmentQuery, {
	type ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragmentQuery,
} from './__generated__/ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragmentQuery.graphql';
import { useTableColumnSortConfigsFromLS } from './useTableColumnSortConfigsFromLS.tsx';
import type { ColumnId, ConfigurableChildIssuesPanelProps } from './types.tsx';
import { TABLE_NAME } from './constants.tsx';

export const ConfigurableChildIssuesPanelWithEntryPoint = ({
	props,
	queries: { childIssuesData },
}: EntryPointProps<
	{ childIssuesData: ui_main_ConfigurableChildIssuesPanelWithEntryPointQuery },
	{},
	ConfigurableChildIssuesPanelProps & { rankFieldJql: string },
	{}
>): React.JSX.Element => {
	const data = usePreloadedQuery<ui_main_ConfigurableChildIssuesPanelWithEntryPointQuery>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			query ui_main_ConfigurableChildIssuesPanelWithEntryPointQuery(
				$cloudId: ID!
				$issueSearchInput: JiraIssueSearchInput!
				$first: Int
				$last: Int
				$before: String
				$after: String
				$amountOfColumns: Int!
				$fieldSetsInput: JiraIssueSearchFieldSetsInput
				$issueKey: String!
			) {
				...ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_data
			}
		`,
		childIssuesData,
	);

	return <ConfigurableChildIssuesPanelWithFragmentWithContainerWidth {...props} data={data} />;
};

type ConfigurableChildIssuesPanelWithFragmentProps = {
	data: ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_data$key;
	rankFieldJql: string;
};

export const ConfigurableChildIssuesPanelWithFragment = ({
	data,
	filterSubtasks,
	rankFieldJql,
	containerWidth,
	columnPickerRef,
	isHideDoneFilterEnabled,
	projectId,
	childIssuesPanelType,
}: ConfigurableChildIssuesPanelProps &
	ConfigurableChildIssuesPanelWithFragmentProps): React.JSX.Element | null => {
	const cloudId = useCloudId();
	const issueKey = useIssueKey();
	const [reduxHideDone] = useUserPreferencesValue(HIDE_DONE_SUBTASKS_FILTER);
	const hideDone =
		fg('key-chin-hide-done-relay-migration') || fg('relay_migration_children_issues_section')
			? (isHideDoneFilterEnabled?.toString() ?? 'false')
			: reduxHideDone;
	const [sortByValueRedux] = useChildIssuesSortBy();
	const [sortByValueRelay] = useChildIssuesSortByRelay();
	const sortByValue = fg('relay_migration_children_issues_section')
		? sortByValueRelay
		: sortByValueRedux;
	const [columnNames, setColumnNames] = useState<ColumnId[]>([]);
	const [sortDirection, setSortDirection] = useState<SortOrder>(SORT_ASC);
	const [sortColumn, setSortColumn] = useState<ColumnId>(rankFieldJql);

	// using projectId as we aim to use this sorting across all the work items in the project since
	// storing work item level sorting will fill up the storage
	const STORAGE_PREFIX = `${projectId}_${TABLE_NAME}`;
	const { setSortConfig, getSortConfig } = useTableColumnSortConfigsFromLS(STORAGE_PREFIX);
	const firstUpdate = useRef(true);
	const { showFlag } = useFlagsService();
	const { userPreferredLayout } = useUserPreferredWorkItemLayout();
	const dispatch = useDispatch();
	const isFullPageIssueView = useIsFullPageIssueView();
	const containerRef = useRef<HTMLDivElement>(null);
	const eventHandlers = useEventHandlers(issueKey);

	const columnPickerApi = useRef<ColumnPickerApi>(null);

	const [isTableFocused, setIsTableFocused] = useState(false);

	const handleTableFocus = (event: React.FocusEvent<HTMLElement>) => {
		if (containerRef.current && containerRef.current.contains(event.target)) {
			setIsTableFocused(true);
		}
	};

	const handleTableBlur = () => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		if (containerRef.current && !containerRef.current.contains(document.activeElement)) {
			setIsTableFocused(false);
		}
	};
	const newJQL = useMemo(
		() => JQLBuilder({ issueKey, hideDone, sortColumn, sortDirection, filterSubtasks }),
		[issueKey, hideDone, sortColumn, sortDirection, filterSubtasks],
	);

	const queryVariables = useMemo(
		() =>
			createConfigurableChildIssuesPanelQueryVariables({
				cloudId,
				jql: newJQL,
				fieldSetIds: columnNames,
				issueKey,
			}),
		// Don't update attributes when newJQL changes
		// The refetch is triggered manually and without suspense
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[cloudId, columnNames, issueKey],
	);

	const [childIssuesPanelData, refetch] = useRefetchableFragment<
		ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragmentQuery,
		ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_data$key
	>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_data on Query
			@argumentDefinitions(
				isRealtimeUpdateEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-issue-view-child-issues-panel-realtime-update.relayprovider"
				}
			)
			@refetchable(
				queryName: "ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragmentQuery"
			) {
				jira @required(action: THROW) {
					issueByKey(cloudId: $cloudId, key: $issueKey) @include(if: $isRealtimeUpdateEnabled) {
						projectField {
							project {
								...NativeIssueTableWithSelectedIssuesProvider_project
							}
						}
					}
					issueSearch(
						cloudId: $cloudId
						issueSearchInput: $issueSearchInput
						first: $first
						last: $last
						before: $before
						after: $after
						fieldSetsInput: $fieldSetsInput
					) @optIn(to: "JiraSpreadsheetComponent-M1") {
						__id
						totalCount
						...NativeIssueTableWithSelectedIssuesProvider_issues
						issueSearchError {
							__typename
						}
					}
					fieldSetViewQueryByProject(cloudId: $cloudId, issueKey: $issueKey)
						@optIn(to: "JiraIssueSearch") {
						... on JiraFieldSetView {
							__typename
							id
							hasDefaultFieldSets
							fieldSets(first: $amountOfColumns, filter: { fieldSetSelectedState: SELECTED }) {
								__id
								...NativeIssueTableWithSelectedIssuesProvider_columns
								...columnPicker_issueViewChildIssuesPanel
							}
							...ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_fieldSetViewQueryByProject
								@arguments(amountOfColumns: $amountOfColumns)
						}
						... on QueryError {
							__typename
						}
					}
				}
				...ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_childIssuesPanelData
			}
		`,
		data,
	);

	const {
		data: issueSearchPaginatedData,
		hasNext,
		hasPrevious,
		isLoadingNext,
		isLoadingPrevious,
		loadNext,
		loadPrevious,
	} = usePaginationFragment<
		ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_IssueSearchPaginationQuery,
		ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_childIssuesPanelData$key
	>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_childIssuesPanelData on Query
			@refetchable(
				queryName: "ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_IssueSearchPaginationQuery"
			) {
				jira @required(action: THROW) {
					issueSearch(
						cloudId: $cloudId
						issueSearchInput: $issueSearchInput
						first: $first
						last: $last
						before: $before
						after: $after
						fieldSetsInput: $fieldSetsInput
					)
						@connection(
							key: "ChildIssuesPanelIssueSearchPagination__issueSearch"
							filters: ["issueSearchInput"]
						)
						@optIn(to: "JiraSpreadsheetComponent-M1") {
						__id
						# eslint-disable-next-line @atlassian/relay/unused-fields
						edges {
							cursor
						}
						pageInfo {
							endCursor
							startCursor
						}
						totalCount
						issueSearchError {
							__typename
						}
						...NativeIssueTableWithSelectedIssuesProvider_issues
					}
				}
			}
		`,
		childIssuesPanelData,
	);

	const {
		issueByKey,
		fieldSetViewQueryByProject,
		issueSearch: issueSearchNonPaginated,
	} = childIssuesPanelData.jira;

	const fieldSetViewMetadata =
		useFragment<ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_fieldSetViewQueryByProject$key>(
			// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
			graphql`
				fragment ui_issueViewCommonViews_ConfigurableChildIssuesPanelWithFragment_fieldSetViewQueryByProject on JiraFieldSetView
				@argumentDefinitions(amountOfColumns: { type: "Int!" }) {
					id
					fieldSets(first: $amountOfColumns, filter: { fieldSetSelectedState: SELECTED }) {
						__id
						...NativeIssueTableWithSelectedIssuesProvider_columns
						...columnPicker_issueViewChildIssuesPanel
					}
				}
			`,
			fieldSetViewQueryByProject?.__typename === 'JiraFieldSetView'
				? fieldSetViewQueryByProject
				: null,
		);

	const onError = useCallback(
		(error: Error) => {
			showFlag(onChildIssueLoadErrorFlag);
			fireErrorAnalytics({
				meta: {
					id: 'loadConfigurableChildIssueTableError',
					packageName: 'jiraIssueViewCommonViews',
					teamName: 'bento',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
				skipSentry: true,
			});
		},
		[showFlag],
	);

	const issues = fg('jira-issue-view-child-issues-panel-pagination')
		? issueSearchPaginatedData.jira.issueSearch
		: issueSearchNonPaginated;

	if (issues?.issueSearchError || fieldSetViewQueryByProject?.__typename === 'QueryError') {
		if (!fg('tnt-3111-ttm-error-handling')) {
			onError(new Error('child issue search GET API error'));
		}

		const error = new Error('child issue search GET API error');

		// Fire specific analytics for this error scenario
		fireErrorAnalytics({
			meta: {
				id: 'childIssueSearchApiError',
				packageName: 'jiraIssueViewCommonViews',
				teamName: 'Team Managed - TNT',
			},
			error,
			sendToPrivacyUnsafeSplunk: true,
			skipSentry: true,
		});

		onError(error);
	}

	const fieldSetViewIdOld =
		fieldSetViewQueryByProject?.__typename === 'JiraFieldSetView'
			? fieldSetViewQueryByProject?.id
			: null;

	const fieldSetViewId = fg('issue-jiv-20390-column-picker-disappearing-fix')
		? (fieldSetViewMetadata?.id ?? null)
		: fieldSetViewIdOld;

	const fieldSetsOld =
		fieldSetViewQueryByProject?.__typename === 'JiraFieldSetView'
			? fieldSetViewQueryByProject?.fieldSets
			: null;

	const fieldSets = fg('issue-jiv-20390-column-picker-disappearing-fix')
		? (fieldSetViewMetadata?.fieldSets ?? null)
		: fieldSetsOld;

	const hasDefaultFieldSets =
		fieldSetViewQueryByProject?.__typename === 'JiraFieldSetView'
			? fieldSetViewQueryByProject?.hasDefaultFieldSets
			: null;

	/**
	 * By default, refetching a Relay refetchable fragment will suspend a component,
	 * so we have a hook tied in with the loading state that fetches without suspending
	 * and disable interactivity with the old result set. In React 18 the workaround
	 * will no longer be needed as any refetch operation inside a transition will not suspend the component.
	 */
	// TODO: Remove when React18 is here
	const { isLoading, refetchWithoutSuspending } = useRefetchWithoutSuspend({
		defaultVariables: queryVariables,
		refetch,
		query: ConfigurableChildIssuesPanelWithFragmentQuery,
		onError,
	});

	const refetchChildIssues = useCallback(() => {
		refetchWithoutSuspending(
			{
				issueSearchInput: {
					jql: newJQL,
				},
			},
			{
				fetchPolicy: 'store-or-network',
			},
		);
	}, [newJQL, refetchWithoutSuspending]);

	/**
	 * When the JQL changes (except for the first update) then refetch data
	 */
	useEffect(() => {
		if (firstUpdate.current) {
			firstUpdate.current = false;
			return;
		}
		refetchChildIssues();
	}, [newJQL, refetchChildIssues]);

	const recentlyCreatedIssue = useSelector(getRecentlyCreatedIssue); // Issue Create
	const previousCreatedIssueKey = usePrevious(recentlyCreatedIssue?.issueKey);

	const recentlyLinkedChildIssue = useSelector(getRecentlyLinkedChildIssue); // Issue Link
	const previousLinkedChildIssueKey = usePrevious(recentlyLinkedChildIssue?.issueKey);

	const onRecentlyIssueCreationSuccess = useCallback(
		(issue: CreatedIssue) => {
			if (issue.usedGlobalCreateDialog || issue.usedAiWorkBreakdown) {
				return;
			}

			const flagId = toFlagId(uuid());
			const issueList: CreatedIssues = [
				{
					issueId: Number(issue.issueId),
					issueKey: issue.issueKey,
				},
			];
			const issueDiscoveryUrl = getIssueDiscoveryUrl(issueList);
			showFlag({
				id: flagId,
				render: (flagProps) => {
					const successFlagProps = {
						key: flagProps.key,
						flagKey: flagProps.key,
						id: flagId,
						issueList,
						issueDiscoveryUrl,
					};
					return <SuccessFlagWithContext {...successFlagProps} />;
				},
			});
		},
		[showFlag],
	);

	/**
	 * When a newly created/linked child issue is detected, refetch the table
	 * and if the issue is successfully created show a success flag
	 */
	useEffect(() => {
		const hasRecentlyCreatedIssue = previousCreatedIssueKey !== recentlyCreatedIssue?.issueKey;
		const hasRecentlyLinkedIssue =
			previousLinkedChildIssueKey !== recentlyLinkedChildIssue?.issueKey;
		if (hasRecentlyCreatedIssue || hasRecentlyLinkedIssue) {
			if (hasRecentlyCreatedIssue && recentlyCreatedIssue) {
				onRecentlyIssueCreationSuccess(recentlyCreatedIssue);
			}
			refetchChildIssues();
		}
	}, [
		previousCreatedIssueKey,
		previousLinkedChildIssueKey,
		recentlyCreatedIssue?.issueKey,
		recentlyLinkedChildIssue?.issueKey,
		refetchChildIssues,
		onRecentlyIssueCreationSuccess,
		recentlyCreatedIssue,
	]);

	useEffect(() => {
		const hasRecentlyCreatedIssue = previousCreatedIssueKey !== recentlyCreatedIssue?.issueKey;
		return () => {
			if (isFullPageIssueView && fg('issue_view_layout_experiment_feature_gate')) {
				if (hasRecentlyCreatedIssue && recentlyCreatedIssue) {
					/*
							When component unmounts in case of collapsable section
							we want to set, previousCreatedIssueKey to undefined so that
							the success toaster does not appear again
						*/
					dispatch(updateRecentCreatedChildIssueOnUnMount());
				}
			}
		};
	}, [previousCreatedIssueKey, dispatch, recentlyCreatedIssue, isFullPageIssueView]);

	/**
	 * When the OrderBy value (sortByValue) changes
	 * then set the sorting accordingly
	 */
	useEffect(() => {
		setSortDirection(SORT_ASC);
		if (!sortByValue || sortByValue === SORT_OPTION.RANKING) {
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
			setSortColumn(rankFieldJql);
		} else {
			setSortColumn(sortByValue);
		}
	}, [sortByValue, rankFieldJql]);

	useEffect(() => {
		if (fg('preserve_column_sort_configuration')) {
			// the hook retrieves the value from LS
			// if return value !-== null, then we set the newDirection and sortColumn

			const { sortColumnFromLS, sortDirectionFromLS } = getSortConfig();

			if (sortColumnFromLS && sortDirectionFromLS) {
				setSortColumn(sortColumnFromLS);
				setSortDirection(sortDirectionFromLS);
			}
		}
	}, [getSortConfig]);

	/**
	 * Establish context for this panel based on the data
	 */
	const hasMultipleIssues = Number(issues?.totalCount ?? 0) > 1;
	const isSimplifiedProject = useSelector(getIsSimplifiedProject);
	// TODO: Remove on cleanup of relay_migration_children_issues_section
	const isChildrenIssuesPanel = useSelector(getIsChildrenIssuesPanel);
	// TODO: Remove on cleanup of relay_migration_children_issues_section
	const isClassicSubtaskPanel = useSelector(getIsClassicSubtaskPanel);

	/**
	 * onSortChange is invoked when the sorting criteria is updated by the user
	 * The issue table will display sorting indicators and tooltips when hovering over a table header (i.e. column name)
	 */
	const onSortChange = useCallback(
		(field: ColumnId, direction: SortOrder) => {
			if (direction === SORT_NONE && fg('jira_child_issues_clear_sorting_fix')) {
				// Resets the sorting to the default sorting
				setSortDirection(SORT_ASC);
				setSortColumn(rankFieldJql);
			} else {
				setSortDirection(direction === SORT_ASC ? SORT_ASC : SORT_DESC);
				setSortColumn(field);
			}

			if (fg('preserve_column_sort_configuration')) {
				// store new sort value in local storage
				setSortConfig(field, direction);
			}
		},
		[setSortConfig, rankFieldJql],
	);

	/**
	 * Determines whether a field set view has already been created
	 * i.e., user has changed column configuration (resize, reorder) in a project
	 */
	const isFieldSetViewCreated =
		fieldSetViewId != null &&
		// Should be okay to parse since `fieldSetViewId` from the ARI is a number
		// See https://developer.atlassian.com/platform/atlassian-resource-identifier/resource-owners/registry/#ati%3Acloud%3Ajira%3Afield-set-view
		Number(JiraFieldSetViewAri.parse(fieldSetViewId).fieldSetViewId) !== DEFAULT_FIELD_SET_VIEW_ID;

	const [onColumnsChange, isColumnsChangeInFlight] = useColumnsChange({
		fieldSetViewId,
		fieldSetsConnectionId: fieldSets?.__id ?? '',
		onCompleted: (
			changeType: ColumnConfigChangeType,
			success: boolean | undefined,
			columnIds: ColumnId[],
		) => {
			if (success && changeType !== REORDER_COLUMNS) {
				setColumnNames(columnIds);
			}
		},
	});

	const onRestoreDefaultColumns = useColumnReset({
		fieldSetViewId,
		onSuccess: () => {
			columnPickerApi.current?.close();
			refetchChildIssues();
		},
	});
	/**
	 * onColumnResize is invoked after a user has completed a resize interaction with a column
	 */
	const [onColumnResize, isColumnResizeInFlight] = useColumnResize({
		fieldSetsId: fieldSets?.__id ?? '',
	});

	/**
	 * onRowReorder is invoked after a user reorders issues via a drag-and-drop operation.
	 *
	 * This callback will only be passed if `canRankIssues` is true
	 *
	 * TODO: Remove on jsc-2065-unify-onrowreorder cleanup
	 */
	const onRowReorder = useIssuesRanker(issues?.__id || '');

	/**
	 * Determines whether a user can reorder issues
	 */
	const canRankIssues = fg('relay_migration_children_issues_section')
		? // Issue ranking is only supported in CMP Subtasks and TMP Child Issues
			(childIssuesPanelType === CHILD_ISSUES_PANEL_TYPE.CLASSIC_SUBTASKS ||
				(isSimplifiedProject &&
					childIssuesPanelType === CHILD_ISSUES_PANEL_TYPE.CHILDREN_ISSUES_PANEL)) &&
			// All issues should be visible
			hideDone !== 'true' &&
			// Should be at least 2 issues
			hasMultipleIssues &&
			// Rank field should be the sort option
			sortColumn === rankFieldJql
		: // Issue ranking is only supported in CMP Subtasks and TMP Child Issues
			(isClassicSubtaskPanel || (isSimplifiedProject && isChildrenIssuesPanel)) &&
			// All issues should be visible
			hideDone !== 'true' &&
			// Should be at least 2 issues
			hasMultipleIssues &&
			// Rank field should be the sort option
			sortColumn === rankFieldJql;

	/**
	 * useColumnLoaderFromIssueKey is invoked when options need to be retrieved for the column picker
	 */
	const useColumnLoaderFromIssueKey: (selectedColumns: SelectValue) => AsyncLoadOptions = (
		selectedColumns: SelectValue,
	) => {
		return useColumnLoader({ issueKey, selectedColumns });
	};

	const isAnonymous = useIsAnonymous();

	/**
	 * Blocks the usage of the column picker
	 */
	const isColumnPickerLoading =
		// When column size and column change mutation is in flight
		(isColumnResizeInFlight || isColumnsChangeInFlight) &&
		// and the field set view is NOT yet created
		!isFieldSetViewCreated &&
		fg('issue-jiv-20390-column-picker-disappearing-fix');

	if (!fieldSets) return null;
	if (!issues || (!issues.totalCount && !isHideDoneFilterEnabled)) {
		logger.unsafeErrorWithCustomerData(
			'issue.child-issue-panel',
			`issue total count is 0 for issue $${issueKey}, has issues: ${!!issues}`,
		);
	}

	const checkContainerWidthPass = fg('issue-jiv-20891-move-container-width-up')
		? true
		: !!(containerWidth && containerWidth > 0);

	return (
		<>
			{columnPickerRef.current != null &&
				!isAnonymous &&
				createPortal(
					<ColumnPickerContainer>
						<ColumnPicker
							columns={fieldSets}
							isLoading={isColumnPickerLoading}
							ref={columnPickerApi}
							onColumnsChange={onColumnsChange}
							onRestoreDefaultColumns={!hasDefaultFieldSets ? onRestoreDefaultColumns : undefined}
							useColumnLoaderProp={useColumnLoaderFromIssueKey}
							{...(fg('jira_list_merged_column_gate') && {
								isMergedColumnEnabled: true,
							})}
						/>
					</ColumnPickerContainer>,
					columnPickerRef.current,
				)}
			{checkContainerWidthPass && !!issues?.totalCount && (
				<IssueContext.Provider value="localStateMode">
					<MaybeIssueTableScrollViewport>
						<IssueTableContainer
							ref={containerRef}
							onFocus={handleTableFocus}
							onBlur={handleTableBlur}
						>
							<ChildIssuesPanelErrorBoundaryWrapper componentName="issue-table">
								<NativeIssueTable
									project={
										fg('jira-issue-view-child-issues-panel-realtime-update')
											? issueByKey?.projectField?.project
											: undefined
									}
									issues={issues}
									sortField={sortColumn}
									sortDirection={sortDirection}
									columns={fieldSets}
									columnConfigOverrides={getColumnConfigOverrides(
										containerWidth,
										userPreferredLayout,
									)}
									onRowReorder={
										canRankIssues && !fg('jsc-2065-unify-onrowreorder') ? onRowReorder : undefined
									}
									onSortChange={onSortChange}
									loading={isLoading}
									{...(!isAnonymous && {
										onColumnResize,
										onColumnsChange,
									})}
									{...(!isAnonymous &&
										!hasDefaultFieldSets && {
											onRestoreDefaultColumns,
										})}
									isColumnPickerLoading={isColumnPickerLoading}
									isIssueOperationMeatballMenuEnabled={false}
									isIssueRankingEnabled={canRankIssues && fg('jsc-2065-unify-onrowreorder')}
									isCompactColumnEnabled
									isEntireRowDraggable
									{...(fg('jira-issue-view-child-issues-panel-realtime-update') && {
										isStatusTrackingEnabled: true,
									})}
									{...(fg('jira-issue-view-child-issues-panel-pagination') && {
										// TODO: Smarter threshold https://jplat.atlassian.net/browse/JIV-20800
										activationThreshold: ROW_HEIGHT * 2,
										onLoadNext: () => loadNext(CHILD_ISSUES_PAGE_SIZE),
										onLoadPrevious: () => loadPrevious(CHILD_ISSUES_PAGE_SIZE),
										isLoadingNext,
										isLoadingPrevious,
										hasNext,
										hasPrevious,
									})}
									{...(fg('jira_list_merged_column_gate') && {
										isMergedColumnEnabled: true,
									})}
									{...(expVal('visual_uplift_for_child_issues_table', 'isEnabled', false) && {
										isChildIssuesTableVisualUpliftEnabled: true,
									})}
									components={{
										columnPicker: {
											Icon: CustomizeIcon,
										},
									}}
									eventHandlers={eventHandlers}
									{...((fg('ken-2879_is_child_issue_table') ||
										fg('issue_view_layout_experiment_feature_gate')) && {
										isChildIssuesTable: true,
									})}
									areShortcutsEnabled={isTableFocused}
									{...(fg('jira_timeline_cell_border_style')
										? {
												cellBorderStyle: 'NO_COLUMN_BORDER',
											}
										: { isColumnBordersEnabled: false })}
								/>
							</ChildIssuesPanelErrorBoundaryWrapper>
						</IssueTableContainer>
					</MaybeIssueTableScrollViewport>
				</IssueContext.Provider>
			)}
		</>
	);
};

// Exported for testing

export const ConfigurableChildIssuesPanelWithFragmentWithContainerWidth = componentWithFG(
	'issue-jiv-20891-move-container-width-up',
	ConfigurableChildIssuesPanelWithFragment,
	withContainerWidth(ConfigurableChildIssuesPanelWithFragment),
);

const IssueTableScrollViewport = ({ children }: PropsWithChildren<{}>) => {
	const maxHeight = HEADER_HEIGHT + ROW_HEIGHT * CHILD_ISSUES_PAGE_SIZE;

	return (
		<Box
			xcss={scrollViewportStyles}
			// eslint-disable-next-line jira/react/no-style-attribute
			style={{ maxHeight: `${maxHeight}px` }}
		>
			{children}
		</Box>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const IssueTableViewPort = styled.div({
	overflow: 'auto',
});

const MaybeIssueTableScrollViewport = componentWithFG(
	'jira-issue-view-child-issues-panel-pagination',
	IssueTableScrollViewport,
	IssueTableViewPort,
);

const BasicIssueTableContainer = forwardRef<
	HTMLDivElement,
	{
		children: React.ReactNode;
		onFocus?: (event: React.FocusEvent<HTMLDivElement>) => void;
		onBlur?: () => void;
	}
>(({ children, onFocus, onBlur }, ref) => (
	<Box ref={ref} onFocus={onFocus} onBlur={onBlur}>
		{children}
	</Box>
));

const IssueTableContainer = BasicIssueTableContainer;

const scrollViewportStyles = xcss({
	display: 'flex',
	flexBasis: 0,
	flexDirection: 'column',
	flexGrow: 1,
	overflow: 'hidden',
});
